/**
 * Calendar Selector Component
 *
 * Wrapper component for selecting calendars and optionally family labels.
 * Uses UnifiedCalendarFilter for the actual UI rendering.
 */

import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { FamilyMember } from '@/types/family-member';
import { UnifiedCalendarFilter } from './filter';

type CalendarSelectorProps = {
  /** Available device calendars */
  calendars: DeviceCalendar[];
  /** Currently selected calendar IDs */
  selectedIds: string[];
  /** Callback when calendar selection changes */
  onChange: (selectedIds: string[]) => void;
  /** Available family members for label filtering */
  familyMembers?: FamilyMember[];
  /** Currently selected family label IDs */
  selectedLabelIds?: string[];
  /** Callback when label selection changes */
  onLabelChange?: (labelIds: string[]) => void;
  /** Whether to show family labels section (default: true if familyMembers provided) */
  showLabels?: boolean;
};

/**
 * CalendarSelector allows users to select which device calendars
 * and family labels to filter events by (multi-select).
 */
export function CalendarSelector({
  calendars,
  selectedIds,
  onChange,
  familyMembers = [],
  selectedLabelIds = [],
  onLabelChange,
  showLabels,
}: CalendarSelectorProps) {
  // Default showLabels to true if familyMembers are provided and onLabelChange exists
  const shouldShowLabels = showLabels ?? (familyMembers.length > 0 && !!onLabelChange);

  const handleLabelChange = (labelIds: string[]) => {
    onLabelChange?.(labelIds);
  };

  return (
    <UnifiedCalendarFilter
      calendars={calendars}
      familyMembers={familyMembers}
      selectedCalendarIds={selectedIds}
      selectedLabelIds={selectedLabelIds}
      onCalendarChange={onChange}
      onLabelChange={handleLabelChange}
      showLabels={shouldShowLabels}
    />
  );
}
